/**
 * OSP access/refresh token pair with expiry checks (aligned with SDK Credentials).
 *
 * @source @open-social-protocol/osp-api-index-types/src/storage/credentials.ts
 */

import { jwtDecode, type JwtPayload } from "utils";

/** Refresh when within this window of access_token expiry (5 minutes). */
export const TOKEN_EXP_THRESHOLD_MS = 300_000;

export interface OspStoredCredentials {
  accessToken?: string;
  refreshToken?: string;
}

export class OspCredentials implements OspStoredCredentials {
  constructor(
    readonly accessToken: string | undefined,
    readonly refreshToken?: string,
  ) {}

  canRefresh(): boolean {
    return !!this.refreshToken;
  }

  shouldRefresh(expTimeMs?: number): boolean {
    const accessToken = this.accessToken;
    if (!accessToken) {
      return true;
    }
    const now = Date.now();
    const tokenExpTimestamp = getAccessTokenExpTimestamp(accessToken);
    const threshold = expTimeMs ?? TOKEN_EXP_THRESHOLD_MS;
    return now >= tokenExpTimestamp - threshold;
  }

  static fromApiTokens(tokens: {
    access_token?: string;
    refresh_token?: string;
  }): OspCredentials {
    return new OspCredentials(tokens.access_token, tokens.refresh_token);
  }
}

export function getAccessTokenExpTimestamp(accessToken: string): number {
  const { exp } = jwtDecode<JwtPayload>(accessToken);
  if (typeof exp !== "number") {
    throw new Error("Invalid JWT: missing exp");
  }
  return exp * 1000;
}
